import Layout from "@/components/Layout";
import PermissionDenied from "@/components/PermissionDenied";
import { ProductImageThumb } from "@/components/catalog/ProductImageThumb";
import { DatePartsPicker } from "@/components/hr/DatePartsPicker";
import { SalePaymentLinesSection } from "@/components/sales/SalePaymentLinesSection";
import { SaleProductPicker } from "@/components/sales/SaleProductPicker";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { formatCurrency } from "@/lib/currencies/currenciesStore";
import { resolveProductThumbnailUrl } from "@/lib/catalog/productThumbnail";
import { pkrAmountShortLabel } from "@/lib/formatPkrAmount";
import { processCarExchange } from "@/lib/sales/carExchangesStore";
import {
  fetchSaleInvoice,
  saleInvoiceErrorMessage,
  type SaleInvoiceRecord,
} from "@/lib/sales/saleInvoiceStore";
import {
  buildSalePaymentsPayload,
  newSalePaymentLine,
  salePaymentLinesTotal,
  validateSalePaymentLine,
  type SalePaymentLine,
} from "@/lib/sales/salePaymentLines";
import type { SaleProductPick } from "@/lib/sales/saleProductSearchStore";
import { cn } from "@/lib/utils";
import { isAxiosError } from "axios";
import { format, isValid, parseISO } from "date-fns";
import { ArrowLeft, FileText, Loader2, RefreshCw, Undo2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

function invoiceLabel(sale: SaleInvoiceRecord): string {
  return sale.invoice_no || String(sale.id);
}

const CarExchangeProcess = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user] = useState(() => getStoredAuthUser());
  const canView = can(user, "sale_can_view") || can(user, "sale_can_view_any");
  const canProcess = can(user, "sale_can_update") || can(user, "sale_can_create");

  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [sale, setSale] = useState<SaleInvoiceRecord | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const [exchangeDate, setExchangeDate] = useState(() => format(new Date(), "yyyy-MM-dd"));
  const [replacement, setReplacement] = useState<SaleProductPick | null>(null);
  const [replacementPriceText, setReplacementPriceText] = useState("");
  const [refundLines, setRefundLines] = useState<SalePaymentLine[]>([]);
  const [collectionLines, setCollectionLines] = useState<SalePaymentLine[]>([]);
  const [notes, setNotes] = useState("");
  const [settlementMethodsError, setSettlementMethodsError] = useState<string | null>(null);

  const replacementPrice = useMemo(() => {
    const n = Number(replacementPriceText);
    return Number.isFinite(n) && replacementPriceText.trim() !== "" ? round2(n) : 0;
  }, [replacementPriceText]);

  useEffect(() => {
    if (!id || !canView) return;
    let mounted = true;
    setLoading(true);
    setForbidden(false);
    void fetchSaleInvoice(id)
      .then((row) => {
        if (!mounted) return;
        setSale(row);
        if (row?.sale_exchange?.notes) setNotes(row.sale_exchange.notes);
        if (row?.sale_exchange?.replacement_sale_amount) {
          const amt = Number(row.sale_exchange.replacement_sale_amount);
          setReplacementPriceText(Number.isFinite(amt) && amt > 0 ? String(amt) : "");
        }
      })
      .catch((err) => {
        if (!mounted) return;
        if (isAxiosError(err) && err.response?.status === 403) {
          setForbidden(true);
          return;
        }
        toast.error(saleInvoiceErrorMessage(err));
        setSale(null);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [id, canView]);

  const isExchanged = Boolean(
    sale?.is_exchanged || sale?.void_type === "exchange" || sale?.payment_status === "exchanged",
  );
  const isCancelled = Boolean(
    sale?.is_cancelled ||
      sale?.cancelled_at ||
      sale?.payment_status === "cancelled" ||
      sale?.is_returned ||
      sale?.void_type === "return" ||
      sale?.payment_status === "returned" ||
      isExchanged,
  );

  const collectedAmount = useMemo(() => {
    if (!sale) return 0;
    if (sale.sale_exchange) return round2(Number(sale.sale_exchange.collected_amount ?? 0));
    const payments = sale.payments ?? [];
    const gross = payments
      .filter((p) => {
        const type = String(p.transaction_type ?? "").toLowerCase();
        return type !== "refund" && Number(p.amount ?? p.credit ?? p.debit ?? 0) > 0.009;
      })
      .reduce((sum, p) => sum + Number(p.amount ?? p.credit ?? p.debit ?? 0), 0);
    if (gross > 0.009) return round2(gross);
    return round2(Number(sale.paid_total ?? 0));
  }, [sale]);

  const originalAmount = round2(Number(sale?.total_amount ?? 0));
  const priceDifference = round2(replacementPrice - originalAmount);
  const appliedCredit = round2(Math.min(collectedAmount, Math.max(0, replacementPrice)));
  const refundDue = round2(Math.max(0, collectedAmount - Math.max(0, replacementPrice)));
  const receivableDue = round2(Math.max(0, Math.max(0, replacementPrice) - collectedAmount));

  const refundTotal = useMemo(() => salePaymentLinesTotal(refundLines), [refundLines]);
  const collectionTotal = useMemo(() => salePaymentLinesTotal(collectionLines), [collectionLines]);
  const refundsMatch = refundDue <= 0.009 || Math.abs(refundTotal - refundDue) < 0.01;
  const collectionsOk = receivableDue <= 0.009 || collectionTotal - receivableDue < 0.01;

  useEffect(() => {
    if (isExchanged || isCancelled) return;
    if (refundDue > 0.009) {
      setRefundLines((prev) => {
        if (prev.length === 1) return [{ ...prev[0], amount: refundDue }];
        if (prev.length === 0) return [newSalePaymentLine(refundDue)];
        return prev;
      });
    } else {
      setRefundLines([]);
    }
  }, [refundDue, isExchanged, isCancelled]);

  useEffect(() => {
    if (isExchanged || isCancelled) return;
    if (receivableDue > 0.009) {
      setCollectionLines((prev) => {
        if (prev.length === 1) return [{ ...prev[0], amount: receivableDue }];
        if (prev.length === 0) return [newSalePaymentLine(receivableDue)];
        return prev;
      });
    } else {
      setCollectionLines([]);
    }
  }, [receivableDue, isExchanged, isCancelled]);

  const onPickReplacement = (product: SaleProductPick | null) => {
    setReplacement(product);
    const price = product?.salePrice != null && Number.isFinite(product.salePrice) ? Number(product.salePrice) : 0;
    setReplacementPriceText(price > 0.009 ? String(round2(price)) : "");
    setSettlementMethodsError(null);
  };

  const validateSettlementMethods = (): string | null => {
    if (refundDue > 0.009) {
      if (refundLines.length === 0) {
        return "Add at least one refund method.";
      }
      if (!refundsMatch) {
        return `Refund methods must total ${formatCurrency(refundDue)}.`;
      }
      for (let i = 0; i < refundLines.length; i++) {
        const otherTotal = salePaymentLinesTotal(refundLines.filter((_, idx) => idx !== i));
        const err = validateSalePaymentLine(refundLines[i], {
          maxAmount: refundDue,
          otherLinesTotal: otherTotal,
        });
        if (err) {
          return err;
        }
      }
    }
    if (receivableDue > 0.009 && collectionTotal > 0.009) {
      if (collectionTotal - receivableDue > 0.009) {
        return `Additional collections cannot exceed ${formatCurrency(receivableDue)}.`;
      }
      for (let i = 0; i < collectionLines.length; i++) {
        if (collectionLines[i].amount <= 0) continue;
        const otherTotal = salePaymentLinesTotal(collectionLines.filter((_, idx) => idx !== i));
        const err = validateSalePaymentLine(collectionLines[i], {
          maxAmount: receivableDue,
          otherLinesTotal: otherTotal,
        });
        if (err) {
          return err;
        }
      }
    }
    return null;
  };

  const openConfirm = () => {
    if (!replacement) {
      toast.error("Select a replacement car from inventory.");
      return;
    }
    if (replacementPrice <= 0.009) {
      toast.error("Enter a valid replacement sale price.");
      return;
    }
    if (sale?.product_id && String(sale.product_id) === replacement.id) {
      toast.error("Replacement car must be different from the original sold car.");
      return;
    }
    const methodsError = validateSettlementMethods();
    if (methodsError) {
      setSettlementMethodsError(methodsError);
      toast.error(methodsError);
      return;
    }
    setSettlementMethodsError(null);
    setConfirmOpen(true);
  };

  const submitExchange = async () => {
    if (!sale || !replacement) return;
    const methodsError = validateSettlementMethods();
    if (methodsError) {
      setSettlementMethodsError(methodsError);
      setConfirmOpen(false);
      toast.error(methodsError);
      return;
    }
    setSaving(true);
    try {
      const refunds = buildSalePaymentsPayload(refundLines).map((line) => ({
        amount: line.amount,
        payment_method: line.payment_method,
        bank_id: line.bank_id,
        transaction_reference_no: line.transaction_reference_no,
        pay_order_no: line.pay_order_no,
        notes: line.notes,
      }));
      const collections = buildSalePaymentsPayload(collectionLines).map((line) => ({
        amount: line.amount,
        payment_method: line.payment_method,
        bank_id: line.bank_id,
        transaction_reference_no: line.transaction_reference_no,
        pay_order_no: line.pay_order_no,
        notes: line.notes,
        payment_mode: line.payment_mode,
      }));
      const primary = refunds[0] ?? collections[0];
      const r = await processCarExchange(sale.id, {
        exchange_date: exchangeDate,
        notes: notes.trim() || "Car exchange — original vehicle restored, replacement sold",
        replacement_product_id: Number(replacement.id),
        replacement_sale_amount: replacementPrice,
        refunds: refundDue > 0.009 ? refunds : undefined,
        collections: collectionTotal > 0.009 ? collections : undefined,
        payment_method: primary?.payment_method ?? null,
        bank_id: primary?.bank_id ?? null,
        transaction_reference_no: primary?.transaction_reference_no ?? null,
        pay_order_no: primary?.pay_order_no ?? null,
      });
      toast.success(r.message);
      setConfirmOpen(false);
      setSettlementMethodsError(null);
      navigate("/car-exchanges");
    } catch (e) {
      const msg = saleInvoiceErrorMessage(e);
      toast.error(msg);
      if (/refund|payment_method|collection/i.test(msg)) {
        setSettlementMethodsError(msg);
        setConfirmOpen(false);
      }
    } finally {
      setSaving(false);
    }
  };

  if (!canView || forbidden) {
    return (
      <Layout title="Car Exchange">
        <PermissionDenied />
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout title="Car Exchange">
        <div className="glass-card p-8 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading car exchange…
        </div>
      </Layout>
    );
  }

  if (!sale) {
    return (
      <Layout title="Car Exchange">
        <div className="space-y-4">
          <div className="glass-card p-8 text-sm text-muted-foreground">Sale not found.</div>
          <Link to="/car-exchanges">
            <Button variant="glass" className="gap-2">
              <ArrowLeft className="h-4 w-4" /> Back to car exchanges
            </Button>
          </Link>
        </div>
      </Layout>
    );
  }

  const invoiceNo = invoiceLabel(sale);
  const productImageUrl = resolveProductThumbnailUrl(sale.product ?? null);
  const exchange = sale.sale_exchange;

  return (
    <Layout title={`Car Exchange ${invoiceNo}`}>
      <div className="w-full space-y-3">
        <div className="glass-card flex flex-col gap-4 p-4 sm:p-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0 space-y-1">
            <div className="flex flex-wrap items-center gap-2.5">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-violet-500/20 bg-violet-500/10 text-violet-700 dark:text-violet-300">
                <RefreshCw className="h-5 w-5" />
              </div>
              <h1 className="font-display text-xl font-bold sm:text-2xl">Car Exchange #{invoiceNo}</h1>
              <span className="rounded-md border border-border/60 bg-muted/40 px-2.5 py-0.5 text-xs font-semibold">
                {isExchanged ? "Already exchanged" : "Exchange processing"}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              Original sale on{" "}
              {sale.date && isValid(parseISO(sale.date))
                ? format(parseISO(sale.date), "MMMM do, yyyy")
                : (sale.date ?? "—")}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Link to="/car-exchanges">
              <Button type="button" variant="outline" size="sm" className="gap-1.5 h-9 text-xs">
                <ArrowLeft className="h-3.5 w-3.5" /> Back
              </Button>
            </Link>
            <Link to={`/product-sales/${sale.id}`}>
              <Button type="button" variant="outline" size="sm" className="gap-1.5 h-9 text-xs">
                <FileText className="h-3.5 w-3.5" /> Open invoice
              </Button>
            </Link>
            {!isCancelled && canProcess ? (
              <Button
                type="button"
                variant="premium"
                size="sm"
                className="gap-1.5 h-9 text-xs"
                onClick={openConfirm}
                disabled={!replacement || !refundsMatch || !collectionsOk}
                title={!replacement ? "Select a replacement car first" : undefined}
              >
                <RefreshCw className="h-3.5 w-3.5" /> Process exchange
              </Button>
            ) : null}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 lg:items-stretch">
          <section className="glass-card overflow-hidden h-full">
            <div className="border-b border-border/30 bg-muted/15 px-4 py-3">
              <h2 className="text-sm font-bold">Original vehicle</h2>
            </div>
            <div className="flex gap-3 p-4">
              <div className="h-20 w-28 overflow-hidden rounded-md border bg-muted">
                <ProductImageThumb src={productImageUrl} alt={sale.product?.name ?? "Product"} />
              </div>
              <div className="min-w-0 space-y-1">
                <p className="font-semibold truncate">{sale.product?.name ?? "—"}</p>
                <p className="text-xs text-muted-foreground">{sale.customer?.name ?? "—"}</p>
                <p className="text-sm font-bold tabular-nums">{formatCurrency(originalAmount)}</p>
                <p className="text-xs text-muted-foreground">
                  Collected {formatCurrency(collectedAmount)}
                </p>
              </div>
            </div>
          </section>

          <section className="glass-card overflow-visible h-full relative z-20">
            <div className="border-b border-border/30 bg-muted/15 px-4 py-3">
              <h2 className="text-sm font-bold">Replacement car (in stock)</h2>
            </div>
            <div className="p-4 space-y-1.5">
              {!isCancelled && canProcess ? (
                <>
                  <SaleProductPicker
                    value={replacement}
                    onChange={onPickReplacement}
                    disabled={saving}
                    inventoryStatus="in_stock"
                    minChars={0}
                    placeholder="Click to list in-stock cars, or type to filter…"
                    minCharsHint="Showing in-stock cars. Type to filter the list."
                    emptyMessage="No in-stock cars available for exchange."
                  />
                  {!replacement ? (
                    <p className="text-[11px] text-amber-700 dark:text-amber-400">
                      Click the field to see in-stock cars, then select one.
                    </p>
                  ) : null}
                </>
              ) : exchange?.replacement_product ? (
                <div className="min-w-0 space-y-1">
                  <p className="font-semibold truncate">{exchange.replacement_product.name}</p>
                  <p className="text-sm font-bold tabular-nums">
                    {formatCurrency(Number(exchange.replacement_sale_amount ?? 0))}
                  </p>
                  {exchange.replacement_sale_id ? (
                    <Link
                      className="text-xs text-primary underline"
                      to={`/product-sales/${exchange.replacement_sale_id}`}
                    >
                      Open replacement sale
                    </Link>
                  ) : null}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No replacement car on this record.</p>
              )}
            </div>
          </section>
        </div>

        {!isCancelled && canProcess ? (
          <section className="glass-card overflow-visible">
            <div className="border-b border-border/30 bg-muted/15 px-4 py-3">
              <h2 className="text-sm font-bold">Process exchange</h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                Review settlement on the left, then set refund or collection on the right
              </p>
            </div>
            <div className="space-y-4 p-4">
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:items-start">
                {/* Left: date, price, settlement */}
                <div className="space-y-4">
                  <div className="rounded-xl border border-border/50 bg-muted/10 p-4 space-y-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="exchange-date">Exchange date</Label>
                      <DatePartsPicker id="exchange-date" value={exchangeDate} onChange={setExchangeDate} disabled={saving} />
                    </div>
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between min-h-6 gap-2">
                        <Label htmlFor="replacement-price">Replacement sale price</Label>
                        {replacementPrice > 0.009 ? (
                          <span className="shrink-0 text-[11px] font-medium leading-none text-primary tabular-nums">
                            {pkrAmountShortLabel(replacementPrice)}
                          </span>
                        ) : null}
                      </div>
                      <Input
                        id="replacement-price"
                        type="number"
                        min={0}
                        step="0.01"
                        inputMode="decimal"
                        className="glass-input h-10"
                        value={replacementPriceText}
                        placeholder="Enter sale price"
                        onChange={(e) => setReplacementPriceText(e.target.value)}
                        disabled={saving || !replacement}
                      />
                    </div>
                  </div>

                  <div className="rounded-xl border border-border/50 bg-card overflow-hidden shadow-sm">
                    <div className="border-b border-border/30 bg-muted/20 px-4 py-2.5">
                      <h3 className="text-sm font-bold">Settlement summary</h3>
                    </div>
                    <div className="space-y-2.5 p-4 text-sm">
                      <div className="flex justify-between gap-2">
                        <span className="text-muted-foreground">Price difference</span>
                        <span
                          className={cn(
                            "font-semibold tabular-nums",
                            priceDifference > 0.009
                              ? "text-amber-700 dark:text-amber-400"
                              : priceDifference < -0.009
                                ? "text-emerald-700 dark:text-emerald-400"
                                : "",
                          )}
                        >
                          {formatCurrency(priceDifference)}
                        </span>
                      </div>
                      <div className="flex justify-between gap-2">
                        <span className="text-muted-foreground">Applied credit</span>
                        <span className="font-semibold tabular-nums">
                          {formatCurrency(isExchanged ? Number(exchange?.applied_credit ?? 0) : appliedCredit)}
                        </span>
                      </div>
                      <div className="flex justify-between gap-2">
                        <span className="text-muted-foreground">Refundable</span>
                        <span className="font-semibold tabular-nums text-emerald-700 dark:text-emerald-400">
                          {formatCurrency(isExchanged ? Number(exchange?.refunded_amount ?? 0) : refundDue)}
                        </span>
                      </div>
                      <div className="flex justify-between gap-2 border-t border-border/40 pt-2.5">
                        <span className="text-muted-foreground">Additional receivable</span>
                        <span className="font-semibold tabular-nums text-amber-700 dark:text-amber-400">
                          {formatCurrency(isExchanged ? Number(exchange?.receivable_amount ?? 0) : receivableDue)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right: refund / collection methods */}
                <div
                  className={cn(
                    "rounded-xl bg-card overflow-hidden shadow-sm min-h-[12rem] border transition-colors",
                    settlementMethodsError
                      ? "border-destructive ring-2 ring-destructive/30"
                      : "border-border/50",
                  )}
                >
                  <div
                    className={cn(
                      "border-b px-4 py-2.5",
                      settlementMethodsError
                        ? "border-destructive/30 bg-destructive/10"
                        : "border-border/30 bg-muted/20",
                    )}
                  >
                    <h3
                      className={cn(
                        "text-sm font-bold",
                        settlementMethodsError && "text-destructive",
                      )}
                    >
                      {refundDue > 0.009
                        ? "Refund methods"
                        : receivableDue > 0.009
                          ? "Additional collection"
                          : "Settlement methods"}
                    </h3>
                    <p
                      className={cn(
                        "text-[11px] mt-0.5",
                        settlementMethodsError ? "text-destructive/90" : "text-muted-foreground",
                      )}
                    >
                      {settlementMethodsError
                        ? settlementMethodsError
                        : refundDue > 0.009
                          ? "Refund the difference to the customer — select a payment method"
                          : receivableDue > 0.009
                            ? "Optional now — unpaid amount stays receivable"
                            : "No cash movement needed for this exchange"}
                    </p>
                  </div>
                  <div className="p-4">
                    {refundDue > 0.009 ? (
                      <SalePaymentLinesSection
                        lines={refundLines}
                        onChange={(lines) => {
                          setRefundLines(lines);
                          setSettlementMethodsError(null);
                        }}
                        expectedTotal={refundDue}
                        salePrice={refundDue}
                        title="Refund methods"
                        addButtonLabel="Add refund method"
                        emptyTitle="No refund methods added."
                        emptyHint="Customer overpaid relative to the replacement price — refund the difference."
                      />
                    ) : receivableDue > 0.009 ? (
                      <SalePaymentLinesSection
                        lines={collectionLines}
                        onChange={(lines) => {
                          setCollectionLines(lines);
                          setSettlementMethodsError(null);
                        }}
                        expectedTotal={receivableDue}
                        salePrice={receivableDue}
                        title="Additional collection"
                        addButtonLabel="Add payment method"
                        emptyTitle="No additional payment recorded."
                        emptyHint="Optional now — unpaid difference remains receivable on the replacement sale."
                      />
                    ) : (
                      <p className="text-xs text-muted-foreground rounded-lg border border-dashed border-border/60 bg-muted/10 px-3 py-3">
                        {replacement
                          ? "Collected credit covers the replacement price exactly — no refund or additional collection required."
                          : "Select a replacement car to see refund or collection options."}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="exchange-notes">Notes (optional)</Label>
                <Textarea
                  id="exchange-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Reason for exchange…"
                  rows={3}
                />
              </div>

              <div className="flex justify-end">
                <Button
                  type="button"
                  variant="premium"
                  className="gap-1.5 h-10 px-5 text-xs font-semibold"
                  onClick={openConfirm}
                  disabled={!replacement || !refundsMatch || !collectionsOk}
                  title={!replacement ? "Select a replacement car first" : undefined}
                >
                  <Undo2 className="h-4 w-4" />
                  Process car exchange
                </Button>
              </div>
            </div>
          </section>
        ) : null}

        {isExchanged && exchange ? (
          <section className="glass-card overflow-hidden">
            <div className="border-b border-border/30 bg-muted/15 px-4 py-3">
              <h2 className="text-sm font-bold">Exchange history</h2>
            </div>
            <div className="grid grid-cols-2 gap-3 p-4 sm:grid-cols-4 text-sm">
              <div>
                <p className="text-[10px] uppercase text-muted-foreground font-semibold">Original</p>
                <p className="font-bold tabular-nums">{formatCurrency(exchange.original_sale_amount)}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase text-muted-foreground font-semibold">Replacement</p>
                <p className="font-bold tabular-nums">{formatCurrency(exchange.replacement_sale_amount)}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase text-muted-foreground font-semibold">Refunded</p>
                <p className="font-bold tabular-nums">{formatCurrency(exchange.refunded_amount)}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase text-muted-foreground font-semibold">Receivable</p>
                <p className="font-bold tabular-nums">{formatCurrency(exchange.receivable_amount)}</p>
              </div>
            </div>
            {exchange.replacement_product ? (
              <p className="px-4 pb-4 text-xs text-muted-foreground">
                Replacement: <span className="text-foreground font-medium">{exchange.replacement_product.name}</span>
                {exchange.replacement_sale_id ? (
                  <>
                    {" · "}
                    <Link className="text-primary underline" to={`/product-sales/${exchange.replacement_sale_id}`}>
                      Open new sale
                    </Link>
                  </>
                ) : null}
              </p>
            ) : null}
          </section>
        ) : null}

        {isCancelled ? (
          <div className="glass-card p-4 text-sm text-muted-foreground">
            {isExchanged
              ? "This sale was exchanged. The original vehicle was restored to stock and a replacement sale was created."
              : "This sale is already cancelled or returned and cannot be exchanged."}
          </div>
        ) : null}
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Process this car exchange?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>
                  Original car returns to stock. Replacement{" "}
                  <strong>{replacement?.name}</strong> will be sold for{" "}
                  <strong>{formatCurrency(replacementPrice)}</strong>.
                </p>
                {refundDue > 0.009 ? <p>Refund to customer: {formatCurrency(refundDue)}</p> : null}
                {receivableDue > 0.009 ? (
                  <p>
                    Additional due: {formatCurrency(receivableDue)}
                    {collectionTotal > 0.009 ? ` (collecting ${formatCurrency(collectionTotal)} now)` : " (left as receivable)"}
                  </p>
                ) : null}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={saving}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => void submitExchange()} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm exchange"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
};

export default CarExchangeProcess;
